import type { JSONContent } from '@tiptap/core';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { InvoiceComment } from 'types/invoiceComments';

export type Composer =
    | { kind: 'new'; opener: HTMLElement | null }
    | { kind: 'reply'; parent: InvoiceComment; opener: HTMLElement | null }
    | { kind: 'edit'; comment: InvoiceComment; opener: HTMLElement | null };

/**
 * What the user is doing in an invoice's comments, kept by the page so it survives the view moving between the
 * drawer and the docked column: the open composer and what is typed in it, the entry Hide handled reveals, the
 * list's scroll and the link already brought into view.
 */
export type CommentsSession = {
    composer: Composer | null;
    openComposer: (composer: Composer | null) => void;
    revealed: number | null;
    setRevealed: (id: number | null) => void;
    draft: { current: JSONContent | undefined };
    /** The list's scroll, for the invoice it was read on. */
    scrollTop: { current: { invoiceId: number; top: number } | null };
    arrivedAt: { current: number | null };
    /** Forgets it all, as the comments close. */
    reset: () => void;
};

/** The comments session of one invoice; another invoice starts a new one. */
export function useCommentsSession(invoiceId: number): CommentsSession {
    const [composer, setComposer] = useState<Composer | null>(null);
    const [revealed, setRevealed] = useState<number | null>(null);
    const draft = useRef<JSONContent | undefined>(undefined);
    const scrollTop = useRef<{ invoiceId: number; top: number } | null>(null);
    const arrivedAt = useRef<number | null>(null);
    const [sessionOf, setSessionOf] = useState(invoiceId);
    if (sessionOf !== invoiceId) {
        setSessionOf(invoiceId);
        setComposer(null);
        setRevealed(null);
    }
    const openComposer = useCallback((next: Composer | null) => {
        draft.current = undefined;
        setComposer(next);
    }, []);
    const reset = useCallback(() => {
        openComposer(null);
        setRevealed(null);
        scrollTop.current = null;
        arrivedAt.current = null;
    }, [openComposer]);
    return useMemo(
        () => ({ composer, openComposer, revealed, setRevealed, draft, scrollTop, arrivedAt, reset }),
        [composer, openComposer, revealed, reset]
    );
}
